<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="show"
        class="email-composition-overlay"
        :class="{ 'dragging-active': !isInitialPosition }"
      >
        <div
          ref="modalRef"
          class="email-composition-modal"
          :class="{ 'is-dragging': isDragging }"
          :style="modalStyle"
          @click.stop
        >
          <div
            class="email-composer__header email-composer__header--draggable"
            @mousedown="startDrag"
            @touchstart="startDrag"
          >
            <div class="email-composer__header-left">
              <span class="email-composer__header-title">New Message</span>
            </div>
            <div class="email-composer__header-right">
              <button
                type="button"
                class="email-composer__header-btn"
                @click="handleClose"
                title="Close"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </div>

          <div class="email-composer__content">
            <form @submit.prevent="handleSend">
              <!-- From -->
              <div class="email-composer__row">
                <div class="email-composer__row-label">
                  <span>From</span>
                  <span class="email-composer__required">*</span>
                </div>
                <div class="email-composer__row-input">
                  <Select
                    v-if="accounts && accounts.length > 0"
                    v-model="formData.account_id"
                    :options="accountOptions"
                    size="sm"
                    required
                  />
                  <div v-else style="padding: 8px 0; color: #646464; font-size: 0.875rem">
                    No email accounts connected. Please connect an account in Settings.
                  </div>
                </div>
              </div>

              <!-- To -->
              <div class="email-composer__row">
                <div class="email-composer__row-label">
                  <span>To</span>
                  <span class="email-composer__required">*</span>
                </div>
                <div class="email-composer__row-input">
                  <div class="email-composer__recipients">
                    <div
                      v-for="(recipient, index) in formData.to"
                      :key="index"
                      class="email-composer__recipient-tag"
                    >
                      <span>{{ recipient.name || recipient.email }}</span>
                      <button
                        type="button"
                        class="email-composer__recipient-remove"
                        @click="removeRecipient(index)"
                      >
                        <Icon icon="mdi:close" />
                      </button>
                    </div>
                    <div class="email-composer__recipient-input-wrapper">
                      <input
                        v-model="recipientInput"
                        type="text"
                        class="email-composer__recipient-input"
                        placeholder="Recipients"
                        @keydown.enter.prevent="addRecipient"
                        @blur="addRecipient"
                      />
                      <button
                        v-if="!showCcBcc"
                        type="button"
                        class="email-composer__cc-toggle"
                        @click="showCcBcc = true"
                      >
                        Cc Bcc
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="showCcBcc" class="email-composer__row">
                <div class="email-composer__row-label">Cc</div>
                <div class="email-composer__row-input">
                  <div class="email-composer__recipients">
                    <div
                      v-for="(recipient, index) in formData.cc"
                      :key="index"
                      class="email-composer__recipient-tag"
                    >
                      <span>{{ recipient.name || recipient.email }}</span>
                      <button
                        type="button"
                        class="email-composer__recipient-remove"
                        @click="removeCCRecipient(index)"
                      >
                        <Icon icon="mdi:close" />
                      </button>
                    </div>
                    <input
                      v-model="ccInput"
                      type="text"
                      class="email-composer__recipient-input"
                      placeholder="Cc"
                      @keydown.enter.prevent="addCCRecipient"
                      @blur="addCCRecipient"
                    />
                  </div>
                </div>
              </div>

              <div v-if="showCcBcc" class="email-composer__row">
                <div class="email-composer__row-label">Bcc</div>
                <div class="email-composer__row-input">
                  <div class="email-composer__recipients">
                    <div
                      v-for="(recipient, index) in formData.bcc"
                      :key="index"
                      class="email-composer__recipient-tag"
                    >
                      <span>{{ recipient.name || recipient.email }}</span>
                      <button
                        type="button"
                        class="email-composer__recipient-remove"
                        @click="removeBCCRecipient(index)"
                      >
                        <Icon icon="mdi:close" />
                      </button>
                    </div>
                    <input
                      v-model="bccInput"
                      type="text"
                      class="email-composer__recipient-input"
                      placeholder="Bcc"
                      @keydown.enter.prevent="addBCCRecipient"
                      @blur="addBCCRecipient"
                    />
                  </div>
                </div>
              </div>

              <div class="email-composer__row">
                <div class="email-composer__row-label">Subject</div>
                <div class="email-composer__row-input">
                  <Input v-model="formData.subject" placeholder="Write a Subject..." size="sm" />
                </div>
              </div>

              <div
                v-if="formData.attachments && formData.attachments.length > 0"
                class="email-composer__attachments"
              >
                <div
                  v-for="(attachment, index) in formData.attachments"
                  :key="index"
                  class="email-composer__attachment-item"
                >
                  <Icon icon="mdi:file-outline" />
                  <span class="email-composer__attachment-name">{{
                    attachment.name || attachment
                  }}</span>
                  <button
                    type="button"
                    class="email-composer__attachment-remove"
                    @click="removeAttachment(index)"
                  >
                    <Icon icon="mdi:close" />
                  </button>
                </div>
              </div>

              <div class="email-composer__body">
                <RichTextEditor
                  ref="richTextEditor"
                  v-model="formData.body"
                  height="300px"
                  placeholder="Compose email"
                  :hide-toolbar-items="['link', 'image', 'video', 'formula', 'clean']"
                />
              </div>

              <!-- Actions -->
              <div class="email-composer__actions">
                <div class="email-composer__actions-left">
                  <div class="email-composer__send-wrapper" ref="sendButtonRef">
                    <div class="email-composer__split-button">
                      <Button
                        variant="primary"
                        size="sm"
                        icon="mdi:send"
                        :loading="isSending || isScheduling"
                        :disabled="!canSend || isSending || isScheduling"
                        @click="handleSend"
                        class="email-composer__send-main-btn"
                      >
                        Send
                      </Button>
                      <button
                        type="button"
                        class="email-composer__send-dropdown-btn"
                        :disabled="!canSend || isSending || isScheduling"
                        @click.stop="toggleScheduleMenu"
                        title="Schedule send"
                        :aria-expanded="showScheduleMenu"
                      >
                        <Icon icon="mdi:chevron-down" />
                      </button>
                    </div>

                    <div
                      v-if="showScheduleMenu && canSend"
                      class="email-composer__schedule-menu"
                      @click.stop
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="mdi:clock-outline"
                        block
                        :disabled="isSending || isScheduling"
                        @click="handleScheduleMenuClick"
                        class="email-composer__schedule-menu-item"
                      >
                        Schedule send
                      </Button>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="email-composer__action-icon-btn"
                    @click="handleAttach"
                    title="Attach files"
                  >
                    <Icon icon="mdi:paperclip" />
                  </button>
                  <div class="email-composer__popover-wrapper" ref="linkPopoverRef">
                    <button
                      type="button"
                      class="email-composer__action-icon-btn"
                      @click="toggleLinkPopover"
                      title="Insert link"
                    >
                      <Icon icon="mdi:link" />
                    </button>
                    <div
                      v-if="showLinkPopover"
                      class="email-composer__popover email-composer__popover--link"
                    >
                      <div class="email-composer__popover-content">
                        <div class="email-composer__link-field-wrapper">
                          <Icon
                            icon="mdi:format-align-left"
                            class="email-composer__link-field-icon"
                          />
                          <input
                            v-model="linkText"
                            type="text"
                            class="email-composer__link-input email-composer__link-input--text"
                            placeholder="Text"
                            @keyup.enter="insertLink"
                            @keyup.esc="closeLinkPopover"
                          />
                        </div>
                        <div
                          class="email-composer__link-field-wrapper email-composer__link-field-wrapper--url"
                        >
                          <Icon icon="mdi:link" class="email-composer__link-field-icon" />
                          <input
                            v-model="linkUrl"
                            type="text"
                            class="email-composer__link-input email-composer__link-input--url"
                            placeholder="Type or paste a link"
                            @keyup.enter="insertLink"
                            @keyup.esc="closeLinkPopover"
                            ref="linkInputRef"
                          />
                          <button
                            type="button"
                            class="email-composer__link-apply-btn"
                            @click="insertLink"
                            :disabled="!linkUrl || linkUrl.trim() === ''"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="email-composer__action-icon-btn"
                    @click="toggleEmojiPopover"
                    title="Insert emoji"
                  >
                    <Icon icon="mdi:emoticon-outline" />
                  </button>
                  <button
                    type="button"
                    class="email-composer__action-icon-btn"
                    @click="handleSaveDraft"
                    :disabled="isSavingDraft"
                    title="Save draft"
                  >
                    <Icon icon="mdi:content-save-outline" />
                  </button>
                  <button
                    type="button"
                    class="email-composer__action-icon-btn"
                    @click="handleDelete"
                    title="Delete draft"
                  >
                    <Icon icon="mdi:delete-outline" />
                  </button>
                </div>
                <div class="email-composer__actions-right">
                  <button
                    ref="aiRefineButtonRef"
                    type="button"
                    class="email-composer__ai-btn"
                    :disabled="isGeneratingAiRefine"
                    :title="isGeneratingAiRefine ? 'Generating...' : 'AI Refine'"
                    @click.stop="showAiRefinePanel = !showAiRefinePanel"
                  >
                    <Icon icon="mdi:sparkles" />
                    <span>AI Refine</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div
          v-if="showEmojiPopover"
          class="email-composer__emoji-container"
          @click.self="closeEmojiPopover"
        >
          <EmojiPicker @select="handleEmojiSelect" />
        </div>
        <AiRefinePopover
          :show="showAiRefinePanel"
          :trigger-el="$refs.aiRefineButtonRef"
          :disabled="isGeneratingAiRefine"
          @generate="handleAiRefineGenerate"
          @close="showAiRefinePanel = false"
        />
      </div>
    </Transition>
    <ActionConfirmationModal
      :show="showDeleteDraftModal"
      title="Delete Draft"
      message="Are you sure you want to delete this draft? This action cannot be undone."
      confirm-text="Delete"
      cancel-text="Cancel"
      icon="mdi:delete-outline"
      @confirm="confirmDeleteDraft"
      @close="showDeleteDraftModal = false"
    />
    <ScheduleSendModal
      :show="showScheduleModal"
      :is-loading="isScheduling"
      @close="showScheduleModal = false"
      @schedule="handleScheduleSend"
    />
  </Teleport>
</template>

<script>
import Button from "@/components/ui/Button.vue";
import Input from "@/components/ui/Input.vue";
import RichTextEditor from "@/components/ui/RichTextEditor.vue";
import Select from "@/components/ui/Select.vue";
import { Icon } from "@iconify/vue";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import ScheduleSendModal from "@/components/socials/ScheduleSendModal.vue";
import EmojiPicker from "@/components/socials/EmojiPicker.vue";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import EmailSignatureService from "@/services/email-signature";
import { getEditorHTML, getQuillInstance } from "@/utils/emailComposerHelpers";
import { useQueryClient } from "@tanstack/vue-query";
import { useToast } from "@/composables/ui/useToast";

export default {
  name: "EmailCompositionModal",
  components: {
    Icon,
    Button,
    Select,
    Input,
    RichTextEditor,
    ActionConfirmationModal,
    ScheduleSendModal,
    EmojiPicker,
    AiRefinePopover,
  },
  setup() {
    const queryClient = useQueryClient();
    const { success, error } = useToast();
    return { queryClient, toastSuccess: success, toastError: error };
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    recipientEmail: {
      type: String,
      default: "",
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    defaultAccountId: {
      type: String,
      default: "",
    },
    initialSubject: {
      type: String,
      default: "",
    },
    initialBody: {
      type: String,
      default: "",
    },
    contactId: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "send", "attach", "schedule", "schedule-send"],
  data() {
    return {
      formData: {
        account_id: "",
        to: [],
        cc: [],
        bcc: [],
        subject: "",
        body: "",
        reply_to: null,
        attachments: [],
        tracking_options: {
          opens: true,
          links: true,
        },
      },
      recipientInput: "",
      ccInput: "",
      bccInput: "",
      isSending: false,
      isSavingDraft: false,
      showCcBcc: false,
      showDeleteDraftModal: false,
      defaultSignature: null,
      showScheduleModal: false,
      showScheduleMenu: false,
      isScheduling: false,
      showLinkPopover: false,
      showEmojiPopover: false,
      linkUrl: "",
      linkText: "",
      showAiRefinePanel: false,
      isGeneratingAiRefine: false,
      // Dragging state
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      modalPosition: { x: 0, y: 0 },
      isInitialPosition: true,
    };
  },
  computed: {
    canSend() {
      const hasBody =
        this.formData.body &&
        (this.formData.body.trim().length > 0 ||
          this.formData.body.replace(/<[^>]*>/g, "").trim().length > 0);
      const hasAccount = this.formData.account_id && this.formData.account_id.trim() !== "";
      return hasAccount && this.formData.to.length > 0 && hasBody;
    },
    accountOptions() {
      return this.accounts.map((account) => {
        const providerLabel = this.getProviderLabel(account.accountType);
        const displayName = account.email || account.name || "Unknown";
        return {
          label: `${displayName}${providerLabel ? ` (${providerLabel})` : ""}`,
          value: account.id,
        };
      });
    },
    modalStyle() {
      if (this.isInitialPosition) {
        return {
          position: "fixed",
          right: "20px",
          bottom: "20px",
          left: "auto",
          top: "auto",
        };
      }

      return {
        position: "fixed",
        left: `${this.modalPosition.x}px`,
        top: `${this.modalPosition.y}px`,
        transform: "none",
        margin: "0",
        right: "auto",
        bottom: "auto",
      };
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(async () => {
          await this.loadDefaultSignature();
          this.initializeForm();
          if (this.initialSubject) this.formData.subject = this.initialSubject;
          if (this.initialBody) {
            this.formData.body = this.initialBody;
            this.$nextTick(() => {
              const editor = this.$refs.richTextEditor;
              if (editor && typeof editor.setHTML === "function") {
                editor.setHTML(this.formData.body);
              }
            });
          }
        });
      } else {
        this.showScheduleMenu = false;
        this.showScheduleModal = false;
        this.showAiRefinePanel = false;
        this.isSending = false;
        this.isScheduling = false;
        document.removeEventListener("click", this.handleClickOutside);
        this.resetPosition();
      }
    },
    showScheduleModal(newVal) {
      if (newVal) {
        this.showScheduleMenu = false;
      } else {
        if (!this.isSending) {
          this.isScheduling = false;
        }
      }
    },
    recipientEmail(newEmail) {
      if (newEmail) {
        this.addRecipientFromString(newEmail, "to");
      }
    },
    defaultAccountId(newVal) {
      if (newVal && !this.formData.account_id) {
        this.formData.account_id = newVal;
      }
    },
  },
  methods: {
    getProviderLabel(accountType) {
      if (!accountType) return "";
      const type = accountType.toUpperCase();
      switch (type) {
        case "GOOGLE":
          return "Gmail";
        case "MICROSOFT":
        case "OUTLOOK":
          return "Microsoft Outlook";
        default:
          return "";
      }
    },
    initializeForm() {
      this.formData = {
        account_id: this.defaultAccountId || (this.accounts.length > 0 ? this.accounts[0].id : ""),
        to: [],
        cc: [],
        bcc: [],
        subject: "",
        body: "",
        reply_to: null,
        attachments: [],
        tracking_options: {
          opens: true,
          links: true,
        },
      };
      this.recipientInput = "";
      this.ccInput = "";
      this.bccInput = "";
      this.showCcBcc = false;

      if (this.recipientEmail) {
        this.addRecipientFromString(this.recipientEmail, "to");
      }

      this.$nextTick(() => {
        this.injectSignatureIntoEditor();
      });
    },
    addRecipient() {
      if (this.recipientInput.trim()) {
        this.addRecipientFromString(this.recipientInput, "to");
        this.recipientInput = "";
      }
    },
    addCCRecipient() {
      if (this.ccInput.trim()) {
        this.addRecipientFromString(this.ccInput, "cc");
        this.ccInput = "";
      }
    },
    addBCCRecipient() {
      if (this.bccInput.trim()) {
        this.addRecipientFromString(this.bccInput, "bcc");
        this.bccInput = "";
      }
    },
    addRecipientFromString(input, field) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const parts = input.split(/[<>]/);
      let email = "";
      let name = "";

      if (parts.length > 1) {
        name = parts[0].trim();
        email = parts[1].trim();
      } else {
        const trimmed = input.trim();
        if (emailRegex.test(trimmed)) {
          email = trimmed;
        } else {
          const words = trimmed.split(/\s+/);
          email = words[words.length - 1];
          name = words.slice(0, -1).join(" ");
        }
      }

      if (email && emailRegex.test(email)) {
        const recipient = { email };
        if (name) recipient.name = name;
        const exists = this.formData[field].some((r) => r.email === email);
        if (!exists) {
          this.formData[field].push(recipient);
        }
      }
    },
    removeRecipient(index) {
      this.formData.to.splice(index, 1);
    },
    removeCCRecipient(index) {
      this.formData.cc.splice(index, 1);
    },
    removeBCCRecipient(index) {
      this.formData.bcc.splice(index, 1);
    },
    removeAttachment(index) {
      this.formData.attachments.splice(index, 1);
    },
    handleAttach() {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.onchange = (e) => {
        const files = Array.from(e.target.files);
        this.formData.attachments.push(...files);
      };
      input.click();
      this.$emit("attach");
    },
    toggleLinkPopover() {
      if (this.showLinkPopover) {
        this.showLinkPopover = false;
      } else {
        this.closeAllPopovers();

        const editor = this.$refs.richTextEditor;
        if (editor) {
          const quill = getQuillInstance(editor);
          if (quill) {
            const range = quill.getSelection(true);
            if (range && range.length > 0) {
              const selectedText = quill.getText(range.index, range.length);
              this.linkText = selectedText.trim();
            }
          }
        }

        this.showLinkPopover = true;
        this.$nextTick(() => {
          const urlInput = this.$refs.linkInputRef;
          if (urlInput) {
            urlInput.focus();
          }
        });
      }
    },
    closeLinkPopover() {
      this.showLinkPopover = false;
      this.linkUrl = "";
      this.linkText = "";
    },
    insertLink() {
      if (!this.linkUrl || this.linkUrl.trim() === "") {
        return;
      }

      const editor = this.$refs.richTextEditor;
      if (!editor) {
        this.closeLinkPopover();
        return;
      }

      try {
        const quill = getQuillInstance(editor);
        if (!quill) {
          this.closeLinkPopover();
          return;
        }

        const range = quill.getSelection(true);
        if (!range) {
          this.closeLinkPopover();
          return;
        }

        let url = this.linkUrl.trim();
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          url = "https://" + url;
        }

        const text = this.linkText.trim() || url;

        if (range.length > 0) {
          quill.formatText(range.index, range.length, "link", url);
        } else {
          quill.insertText(range.index, text, "link", url);
          quill.setSelection(range.index + text.length);
        }

        this.closeLinkPopover();
      } catch (error) {
        this.closeLinkPopover();
      }
    },
    toggleEmojiPopover() {
      if (this.showEmojiPopover) {
        this.showEmojiPopover = false;
      } else {
        this.closeAllPopovers();
        this.showEmojiPopover = true;
      }
    },
    closeEmojiPopover() {
      this.showEmojiPopover = false;
    },
    handleEmojiSelect(emoji) {
      const editor = this.$refs.richTextEditor;
      if (!editor) {
        this.closeEmojiPopover();
        return;
      }

      try {
        const quill = getQuillInstance(editor);
        if (!quill) {
          this.closeEmojiPopover();
          return;
        }

        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength() - 1;

        const emojiChar = emoji.emoji || emoji.native || emoji;
        quill.insertText(index, emojiChar);
        quill.setSelection(index + emojiChar.length);

        this.closeEmojiPopover();
      } catch (error) {
        this.closeEmojiPopover();
      }
    },
    closeAllPopovers() {
      this.showLinkPopover = false;
      this.showEmojiPopover = false;
      this.showScheduleMenu = false;
    },
    handleDelete() {
      this.showDeleteDraftModal = true;
    },
    async handleAiRefineGenerate(payload) {
      if (this.isGeneratingAiRefine) return;
      this.isGeneratingAiRefine = true;
      try {
        const currentDraft = getEditorHTML(this.$refs.richTextEditor, this.formData.body);
        const response = await AIGenerationService.generateRefine({
          contactId: this.contactId || "",
          channel: "email",
          currentDraft,
          currentSubject: this.formData.subject || undefined,
          customInstructions: payload.customInstructions || "",
          files: payload.attachments,
        });

        this.formData.body = response.body;
        if (response.subject) {
          this.formData.subject = response.subject;
        }

        this.$nextTick(() => {
          if (this.$refs.richTextEditor) {
            try {
              const quill = getQuillInstance(this.$refs.richTextEditor);
              if (quill && quill.root) {
                quill.root.innerHTML = response.body;
              }
            } catch (err) {
              console.warn("Error updating Quill editor:", err);
            }
          }
        });

        this.showAiRefinePanel = false;
        this.toastSuccess("Email content refined");
      } catch (error) {
        console.error("Failed to refine email content:", error);
        this.toastError("Failed to refine content");
      } finally {
        this.isGeneratingAiRefine = false;
      }
    },
    confirmDeleteDraft() {
      this.showDeleteDraftModal = false;
      this.handleClose();
    },
    toggleScheduleMenu() {
      this.showScheduleMenu = !this.showScheduleMenu;
      if (this.showScheduleMenu) {
        this.$nextTick(() => {
          document.addEventListener("click", this.handleClickOutside);
        });
      } else {
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
    handleScheduleMenuClick() {
      this.showScheduleMenu = false;
      this.showScheduleModal = true;
    },
    async handleScheduleSend(scheduleData) {
      if (this.isScheduling) {
        return;
      }

      try {
        this.isScheduling = true;
        const editor = this.$refs.richTextEditor;
        let htmlBody = this.formData.body;
        if (editor && typeof editor.getHTML === "function") {
          htmlBody = editor.getHTML() || this.formData.body;
        }

        const emailData = {
          account_id: this.formData.account_id,
          to: this.formData.to,
          body: htmlBody,
          scheduledAt: scheduleData.scheduledAt,
        };

        if (this.formData.cc.length > 0) emailData.cc = this.formData.cc;
        if (this.formData.bcc.length > 0) emailData.bcc = this.formData.bcc;
        if (this.formData.subject) emailData.subject = this.formData.subject;
        if (this.formData.reply_to) emailData.reply_to = this.formData.reply_to;

        if (this.formData.attachments && this.formData.attachments.length > 0) {
          emailData.attachments = this.formData.attachments;
        }

        if (this.formData.tracking_options) {
          const tracking = this.formData.tracking_options;
          if (
            tracking.opens ||
            tracking.links ||
            tracking.open_tracking ||
            tracking.click_tracking
          ) {
            emailData.tracking_options = {
              opens: tracking.opens ?? tracking.open_tracking ?? false,
              links: tracking.links ?? tracking.click_tracking ?? false,
            };
          }
        }

        this.$emit("schedule-send", emailData);
        setTimeout(() => {
          if (this.isScheduling && this.show) {
            this.isScheduling = false;
            this.showScheduleModal = false;
          }
        }, 30000);
      } catch (error) {
        console.error("Error scheduling send:", error);
        this.isScheduling = false;
        this.showScheduleModal = false;
        throw error;
      }
    },
    handleClickOutside(event) {
      if (
        this.showLinkPopover &&
        this.$refs.linkPopoverRef &&
        !this.$refs.linkPopoverRef.contains(event.target)
      ) {
        this.closeLinkPopover();
      }

      if (
        this.showScheduleMenu &&
        this.$refs.sendButtonRef &&
        !this.$refs.sendButtonRef.contains(event.target)
      ) {
        this.showScheduleMenu = false;
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
    async handleSend() {
      if (!this.canSend || this.isSending) return;

      this.isSending = true;
      try {
        const editor = this.$refs.richTextEditor;
        let htmlBody = this.formData.body;
        if (editor && typeof editor.getHTML === "function") {
          htmlBody = editor.getHTML() || this.formData.body;
        }

        const emailData = {
          account_id: this.formData.account_id,
          to: this.formData.to,
          body: htmlBody,
        };

        if (this.formData.cc.length > 0) emailData.cc = this.formData.cc;
        if (this.formData.bcc.length > 0) emailData.bcc = this.formData.bcc;
        if (this.formData.subject) emailData.subject = this.formData.subject;
        if (this.formData.reply_to) emailData.reply_to = this.formData.reply_to;

        if (this.formData.attachments && this.formData.attachments.length > 0) {
          emailData.attachments = this.formData.attachments;
        }

        if (this.formData.tracking_options) {
          const tracking = this.formData.tracking_options;
          if (
            tracking.opens ||
            tracking.links ||
            tracking.open_tracking ||
            tracking.click_tracking
          ) {
            emailData.tracking_options = {
              opens: tracking.opens ?? tracking.open_tracking ?? false,
              links: tracking.links ?? tracking.click_tracking ?? false,
            };
          }
        }

        this.$emit("send", emailData);
        setTimeout(() => {
          if (this.isSending && this.show) {
            this.isSending = false;
          }
        }, 30000);
      } catch (error) {
        console.error("Failed to send email:", error);
        this.isSending = false;
        throw error;
      }
    },
    async handleSaveDraft() {
      if (!this.formData.account_id || this.formData.to.length === 0) return;

      this.isSavingDraft = true;
      try {
        const editor = this.$refs.richTextEditor;
        let htmlBody = this.formData.body;
        if (editor && typeof editor.getHTML === "function") {
          htmlBody = editor.getHTML() || this.formData.body;
        }
      } catch (error) {
        console.error("Failed to save draft:", error);
      } finally {
        this.isSavingDraft = false;
      }
    },
    handleClose() {
      this.$emit("close");
    },
    async loadDefaultSignature() {
      try {
        // First check TanStack Query cache for instant loading
        const cachedSignature = this.queryClient.getQueryData(["defaultEmailSignature"]);
        if (cachedSignature) {
          this.defaultSignature = cachedSignature;
          return;
        }

        // Fallback to API call if not cached
        const response = await EmailSignatureService.getDefaultSignature();
        if (response?.data?.status && response.data.data) {
          const signatureData = response.data.data;
          if (Array.isArray(signatureData) && signatureData.length > 0) {
            this.defaultSignature = signatureData[0];
          } else if (signatureData && typeof signatureData === "object" && signatureData.content) {
            this.defaultSignature = signatureData;
          } else {
            this.defaultSignature = null;
          }
          // Update cache for future use
          if (this.defaultSignature) {
            this.queryClient.setQueryData(["defaultEmailSignature"], this.defaultSignature);
          }
        } else {
          this.defaultSignature = null;
        }
      } catch (error) {
        console.error("Error loading default signature:", error);
        this.defaultSignature = null;
      }
    },
    injectSignatureIntoEditor() {
      if (!this.defaultSignature || !this.defaultSignature.content) {
        return false;
      }

      const editor = this.$refs.richTextEditor;
      if (!editor) {
        return false;
      }

      try {
        const quill = getQuillInstance(editor);
        if (!quill) {
          return false;
        }

        const currentContent = quill.root.innerHTML || "";
        const signatureHtml = this.defaultSignature.content.trim();

        if (!signatureHtml) {
          return false;
        }

        const normalizedCurrent = currentContent.replace(/\s+/g, " ").trim();
        const normalizedSignature = signatureHtml.replace(/\s+/g, " ").trim();

        if (normalizedCurrent.includes(normalizedSignature)) {
          return true;
        }

        let currentHtml = currentContent || this.formData.body || "";

        if (currentHtml === "<p><br></p>" || currentHtml === "<p></p>" || !currentHtml.trim()) {
          currentHtml = "";
        }

        // Remove horizontal lines from signature and leading empty space
        let cleanSignature = signatureHtml.replace(/<hr[^>]*>/gi, "");
        cleanSignature = cleanSignature.replace(/^(<p><br><\/p>|<p><\/p>|<br>|\s)*/i, "");

        let updatedHtml;
        if (currentHtml.trim()) {
          updatedHtml = currentHtml + "<p><br></p>" + cleanSignature;
        } else {
          // Only one empty line instead of two, so there is no excess space
          updatedHtml = "<p><br></p>" + cleanSignature;
        }
        this.formData.body = updatedHtml;

        this.$nextTick(() => {
          if (typeof editor.setHTML === "function") {
            editor.setHTML(updatedHtml);
          }
          if (editor.getHTML) {
            this.formData.body = editor.getHTML() || updatedHtml;
          }
        });

        return true;
      } catch (error) {
        console.error("Error injecting signature into editor:", error);
        return false;
      }
    },
    // Dragging methods
    startDrag(event) {
      const target = event.target;
      const excludedSelectors = [
        ".email-composer__header-btn",
        ".email-composer__content",
        ".email-composer__row",
        ".email-composer__body",
        ".email-composer__actions",
      ];

      if (excludedSelectors.some((selector) => target.closest(selector))) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.isDragging = true;

      const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
      const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

      if (this.isInitialPosition) {
        const modal = this.$refs.modalRef;
        if (modal) {
          const rect = modal.getBoundingClientRect();
          this.modalPosition = { x: rect.left, y: rect.top };
        } else {
          this.modalPosition = {
            x: (window.innerWidth - 600) / 2,
            y: (window.innerHeight - 600) / 2,
          };
        }
        this.isInitialPosition = false;
      }

      this.dragStart = {
        x: clientX - this.modalPosition.x,
        y: clientY - this.modalPosition.y,
      };

      document.addEventListener("mousemove", this.handleDrag);
      document.addEventListener("mouseup", this.stopDrag);
      document.addEventListener("touchmove", this.handleDrag);
      document.addEventListener("touchend", this.stopDrag);
    },
    handleDrag(event) {
      if (!this.isDragging) return;

      event.preventDefault();
      event.stopPropagation();

      const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
      const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

      let newX = clientX - this.dragStart.x;
      let newY = clientY - this.dragStart.y;

      const modal = this.$refs.modalRef;
      if (modal) {
        const rect = modal.getBoundingClientRect();
        const padding = 16;
        newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
        newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
      }

      this.modalPosition = { x: newX, y: newY };
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.handleDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchmove", this.handleDrag);
      document.removeEventListener("touchend", this.stopDrag);
    },
    resetPosition() {
      this.isInitialPosition = true;
      this.modalPosition = { x: 0, y: 0 };
    },
  },
  mounted() {
    if (this.show) {
      this.initializeForm();
    }
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("touchmove", this.handleDrag);
    document.removeEventListener("touchend", this.stopDrag);
  },
};
</script>

<style scoped>
.email-composition-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 10060;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.email-composition-overlay.dragging-active {
  align-items: flex-start;
  justify-content: flex-start;
}

.email-composition-modal {
  width: 600px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  user-select: none;
  transition: box-shadow 0.2s ease;
}

.email-composition-modal.is-dragging {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  cursor: grabbing;
  transition: none;
}

.email-composer__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  font-size: 0.8125rem;
}

.email-composer__header--draggable {
  cursor: move;
  cursor: grab;
  user-select: none;
}

.email-composer__header--draggable:active {
  cursor: grabbing;
}

.email-composer__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.email-composer__header-title {
  font-weight: 500;
  color: #202124;
  font-size: 0.875rem;
}

.email-composer__header-right {
  display: flex;
  gap: 4px;
}

.email-composer__header-btn {
  padding: 4px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5f6368;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.email-composer__header-btn:hover {
  background: #f1f3f4;
  color: #202124;
}

.email-composer__content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.email-composer__row {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  min-height: 48px;
}

.email-composer__row-label {
  width: 80px;
  padding: 12px 16px;
  font-size: 0.8125rem;
  color: #5f6368;
  font-weight: 500;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.email-composer__row-input {
  flex: 1;
  padding: 8px 16px 8px 0;
  display: flex;
  align-items: center;
}

.email-composer__required {
  color: #ea4335;
}

.email-composer__recipients {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
  align-items: center;
  min-height: 32px;
}

.email-composer__recipient-input-wrapper {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 200px;
  gap: 4px;
}

.email-composer__recipient-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e8f0fe;
  border: 1px solid #cce0fc;
  border-radius: 16px;
  font-size: 0.8125rem;
  color: #1a73e8;
}

.email-composer__recipient-remove {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #1a73e8;
  display: flex;
  align-items: center;
  font-size: 16px;
  margin-left: 4px;
}

.email-composer__recipient-remove:hover {
  color: #1557b0;
}

.email-composer__recipient-input {
  flex: 1;
  min-width: 100px;
  border: none;
  outline: none;
  padding: 4px 0;
  font-size: 0.875rem;
  color: #202124;
  background: transparent;
}

.email-composer__recipient-input::placeholder {
  color: #5f6368;
}

.email-composer__cc-toggle {
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: #1a73e8;
  font-size: 0.8125rem;
  cursor: pointer;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
}

.email-composer__cc-toggle:hover {
  background: #f1f3f4;
  border-radius: 4px;
}

.email-composer__attachments {
  padding: 8px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.email-composer__attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.8125rem;
}

.email-composer__attachment-name {
  flex: 1;
  color: #202124;
}

.email-composer__attachment-remove {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5f6368;
  display: flex;
  align-items: center;
  font-size: 16px;
}

.email-composer__attachment-remove:hover {
  color: #202124;
}

.email-composer__body {
  min-height: 270px;
  position: relative;
  overflow: visible;
}

.email-composer__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #fff;
  position: relative;
  overflow: visible;
}

.email-composer__actions-left,
.email-composer__actions-right {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.email-composer__action-icon-btn {
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5f6368;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.email-composer__action-icon-btn:hover:not(:disabled) {
  background: #f1f3f4;
  color: #202124;
}

.email-composer__action-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.email-composer__ai-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  background: rgba(107, 114, 128, 0.12);
  color: #4b5563;
}

.email-composer__ai-btn:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.2);
  color: #374151;
}

.email-composer__ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.email-composer__send-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.email-composer__split-button {
  display: inline-flex;
  align-items: stretch;
  position: relative;
  border-radius: 6px;
  overflow: hidden;
}

.email-composer__send-main-btn {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  padding-right: 8px !important;
  margin: 0 !important;
}

.email-composer__send-dropdown-btn {
  width: 32px;
  height: 100%;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #3e3efe;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.2s;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
}

.email-composer__send-dropdown-btn:hover:not(:disabled) {
  background: #3230d6;
  color: #ffffff;
}

.email-composer__send-dropdown-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.email-composer__schedule-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 10001;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  overflow: hidden;
}

.email-composer__schedule-menu-item {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  padding: 8px 12px;
  gap: 4px;
}

.email-composer__schedule-menu-item:hover {
  background: #f1f3f4;
}

.email-composer__schedule-menu-item :deep(.ui-button__icon) {
  margin-right: 0;
  font-size: 16px;
}

.email-composer__popover-wrapper {
  position: relative;
  z-index: 1;
}

.email-composer__popover {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 10000;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.email-composer__popover--link {
  width: 240px;
  min-width: 240px;
  max-width: 240px;
}

.email-composer__emoji-container {
  position: absolute;
  bottom: 60px;
  left: 16px;
  z-index: 10002;
  pointer-events: auto;
}

.email-composer__popover-content {
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.email-composer__link-field-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

.email-composer__link-field-wrapper--url {
  gap: 6px;
  position: relative;
}

.email-composer__link-field-icon {
  font-size: 16px;
  color: #6b7280;
  flex-shrink: 0;
}

.email-composer__link-input {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 2px !important;
  font-size: 12px;
  outline: none;
  line-height: 1.3;
  transition: border-color 0.2s;
  min-width: 0;
  box-sizing: border-box;
  min-height: 0;
  max-height: 28px;
}

.email-composer__link-input--text:focus {
  border-color: #3e3efe;
}

.email-composer__link-input--url {
  padding-right: 50px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.email-composer__link-input--url:focus {
  border-color: #3e3efe;
}

.email-composer__link-apply-btn {
  position: absolute;
  right: 2px;
  top: 2px;
  bottom: 2px;
  padding: 0 8px;
  border: none;
  border-radius: 2px !important;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1;
  background: #3e3efe;
  color: #ffffff;
  display: flex;
  align-items: center;
  white-space: nowrap;
  min-height: 0;
  max-height: 24px;
}

.email-composer__link-apply-btn:hover:not(:disabled) {
  background: #2e2ed8;
}

.email-composer__link-apply-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

:deep(.ui-input) {
  box-shadow: none;
}

:deep(.ui-input__container) {
  box-shadow: none;
}

:deep(.ui-input__field) {
  border: none;
  padding: 8px 0;
  font-size: 0.875rem;
  box-shadow: none !important;
}

:deep(.ui-input__field:focus) {
  box-shadow: none !important;
  border: none;
}

:deep(.ui-input__field:hover) {
  box-shadow: none !important;
  border: none;
}

:deep(.ui-input__field:hover:not(:disabled)) {
  box-shadow: none !important;
  border: none;
}

:deep(.ui-select__field) {
  border: none;
  padding: 8px 0;
  font-size: 0.875rem;
}

:deep(.ui-select__field:focus) {
  box-shadow: none;
  border: none;
}

/* Rich text editor */
:deep(.rich-text-editor) {
  border: none;
}

:deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid #e5e7eb;
  padding: 4px 8px;
}

:deep(.ql-container) {
  border: none;
  font-size: 0.875rem;
}

:deep(.ql-editor) {
  font-size: 0.875rem;
  padding: 12px 16px;
  min-height: 200px;
  color: #202124;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
